import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram, Eye } from 'lucide-react';
import { revokeSiteAccess } from '../pages/ComingSoon';
import AppActions from './AppActions';
import DirectoryBadges from './DirectoryBadges';
import '../styles/Footer.css';

const TikTokIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Scroll to top on click
const scrollToTop = () => {
  window.scrollTo(0, 0);
};

// Reset preview access and go to welcome page
const resetPreviewAccess = () => {
  revokeSiteAccess();
  window.location.href = '/';
};

const Footer = () => {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <img src="/cadence-foil-logo.png" alt="CADENCE Medical Consulting" className="footer-logo-image" />
            </div>
            <p className="footer-description">
              Clinical Advocacy & Decisive Empowerment, Navigating Care & Expertise.
              <br />
              <span className="footer-tagline">— Your Patient Advocacy & Health Equity Experts</span>
            </p>
            <div className="social-links">
              <a href="https://www.tiktok.com/@cadencemedicalconsulting" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <TikTokIcon />
              </a>
              <a href="https://www.linkedin.com/company/cadencemedicalconsulting/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin">
                <Linkedin size={20} />
              </a>
              <a href="https://www.instagram.com/cadencemedicalconsulting" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram size={20} />
              </a>
            </div>
          </div>

          <div className="footer-section">
            <h3 className="footer-title">Services</h3>
            <ul className="footer-links">
              <li><Link to="/why-we-exist" onClick={scrollToTop}>Diagnostic Review</Link></li>
              <li><Link to="/why-we-exist" onClick={scrollToTop}>Advocacy Launch Session</Link></li>
              <li><Link to="/why-we-exist" onClick={scrollToTop}>Deep Dive Consultation</Link></li>
              <li><Link to="/membership" onClick={scrollToTop}>Membership Plans</Link></li>
              <li><Link to="/affiliate" onClick={scrollToTop}>Affiliate Program</Link></li>
              <li><Link to="/login" onClick={scrollToTop} className="portal-link" data-testid="footer-patient-portal">Patient Portal</Link></li>
              <li><Link to="/admin" onClick={scrollToTop} className="portal-link" data-testid="footer-admin-portal">Admin Portal</Link></li>
              <li><Link to="/welcome" onClick={scrollToTop} className="preview-link" data-testid="footer-preview-landing">Preview Landing</Link></li>
            </ul>
          </div>

          <div className="footer-section">
            <h3 className="footer-title">CADENCE Compass</h3>
            <ul className="footer-links">
              <li><Link to="/about-us" onClick={scrollToTop}>The CADENCE Mission</Link></li>
              <li><Link to="/demo" onClick={scrollToTop} data-testid="footer-demo">See How It Works</Link></li>
              <li><Link to="/articles" onClick={scrollToTop}>Articles & Insights</Link></li>
              <li><Link to="/articles#hirsutism-pathway" onClick={scrollToTop}>Hirsutism Pathway Guide</Link></li>
              <li><Link to="/articles?category=thyroid" onClick={scrollToTop}>Thyroid Health</Link></li>
              <li><Link to="/articles?category=veterans" onClick={scrollToTop}>Veterans Care</Link></li>
            </ul>
            <h3 className="footer-title footer-title-faq">FAQs</h3>
            <ul className="footer-links">
              <li><Link to="/faq" onClick={scrollToTop}>Frequently Asked Questions</Link></li>
            </ul>
          </div>

          <div className="footer-section">
            <h3 className="footer-title">Contact</h3>
            <ul className="footer-contact">
              <li>
                <Mail size={18} />
                <span>CADENCE@cadencemedicalconsulting.com</span>
              </li>
              {/* Phone number hidden until voicemail workflow decided
              <li>
                <Phone size={18} />
                <span>(223) 252-0409</span>
              </li>
              */}
              <li>
                <MapPin size={18} />
                <span style={{ wordBreak: 'normal', overflowWrap: 'normal', hyphens: 'none' }}>
                  Serving patients in Delaware, Maryland &amp; South&nbsp;Carolina via telehealth
                </span>
              </li>
            </ul>
            <Link to="/contact" className="footer-quote-link" onClick={scrollToTop} data-testid="footer-contact-link">
              Request a Consultation
            </Link>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-legal">
            <p className="footer-copyright-line">
              &copy; 2026 CADENCE Medical Consulting, LLC. All rights reserved.
            </p>
            <p className="footer-acronym-credit" aria-label="CADENCE acronym">
              Clinical Advocacy &middot; Decisive Empowerment &middot; Navigating Care &amp; Expertise
            </p>
            <div className="legal-links">
              <Link to="/terms" onClick={scrollToTop}>Terms of Service</Link>
              <Link to="/privacy" onClick={scrollToTop}>Refund Policy</Link>
              <Link to="/hipaa" onClick={scrollToTop}>HIPAA Compliance</Link>
              <button 
                onClick={resetPreviewAccess} 
                className="preview-welcome-btn"
                title="View Welcome/Coming Soon page"
              >
                <Eye size={14} />
                Preview Welcome
              </button>
            </div>
          </div>
          {/* Install + Update controls */}
          <AppActions />
          {/* Professional directory trust badges — auto-renders once any
              directory URL is set in DirectoryBadges.jsx config */}
          <DirectoryBadges />
          <p className="footer-disclaimer">
            CADENCE Medical Consulting, LLC provides informational and advocacy services. We do not replace your primary healthcare providers or emergency medical services.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;